import { workflowStatusService } from '../../services/workflow-status.service'
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { MATCH_AUTO, MATCH_NONE } from '../../constants/matching-algorithms'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { mailRuleService } from '../../services/mail-rule.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { storagePathService } from '../../services/storage-path.service'
import {
  ApiResponse,
  DocumentSource,
  ScheduleDateField,
  TriggerFilterType,
  WorkflowStatus,
  WorkflowTrigger,
  WorkflowTriggerFilter,
  WorkflowTriggerType,
} from '../../types'
import { getMatchingAlgorithms } from '../../utils/matchingAlgorithms'
import CustomFieldsQueryDropdown, {
  CustomFieldQueriesModel,
  CustomFieldQueryElement,
  CustomFieldQueryElementType,
} from '../common/CustomFieldsQueryDropdown'
import { IfPermissions } from '../common/IfPermissions'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputTags } from '../common/InputTags'
import { InputText } from '../common/InputText'
import './TriggerEditDialog.scss'

// Removed CustomFieldQueryFilterEditor - now using CustomFieldsQueryDropdown with useDropdown={false}

interface TriggerEditDialogProps {
  trigger?: WorkflowTrigger | null
  onSuccess: (trigger: WorkflowTrigger) => void
  onError: (error: any) => void
  onClose: () => void
  onDelete?: () => void
}

export const TriggerEditDialog: React.FC<TriggerEditDialogProps> = ({ trigger, onSuccess, onError, onClose, onDelete }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const FILTER_TYPES = useMemo(
    () => [
      { value: TriggerFilterType.TagsAny, label: t('filter_tags_any') },
      { value: TriggerFilterType.TagsAll, label: t('filter_tags_all') },
      { value: TriggerFilterType.TagsNone, label: t('filter_tags_none') },
      { value: TriggerFilterType.CorrespondentIs, label: t('filter_correspondent_is') },
      { value: TriggerFilterType.CorrespondentNot, label: t('filter_correspondent_not') },
      { value: TriggerFilterType.DocumentTypeIs, label: t('filter_document_type_is') },
      { value: TriggerFilterType.DocumentTypeNot, label: t('filter_document_type_not') },
      { value: TriggerFilterType.StoragePathIs, label: t('filter_storage_path_is') },
      { value: TriggerFilterType.StoragePathNot, label: t('filter_storage_path_not') },
      { value: TriggerFilterType.CustomFieldQuery, label: t('filter_custom_field_query') },
    ],
    [t]
  )

  const TRIGGER_TYPES = useMemo(
    () => [
      { value: WorkflowTriggerType.Consumption, label: t('consumption') },
      { value: WorkflowTriggerType.DocumentAdded, label: t('document_added') },
      { value: WorkflowTriggerType.DocumentUpdated, label: t('document_updated') },
      { value: WorkflowTriggerType.Scheduled, label: t('scheduled') },
      { value: WorkflowTriggerType.DocumentWorkflowStatusChanged, label: t('trigger_type_document_workflow_status_changed') },
    ],
    [t]
  )

  const SOURCE_OPTIONS = useMemo(
    () => [
      { value: DocumentSource.ConsumeFolder, label: t('consume_folder') },
      { value: DocumentSource.ApiUpload, label: t('api_upload') },
      { value: DocumentSource.MailFetch, label: t('mail_fetch') },
      { value: DocumentSource.WebUI, label: t('web_ui') },
      { value: DocumentSource.SefFetch, label: t('sef_fetch') },
      { value: DocumentSource.EOtpremnicaFetch, label: t('eotpremnica_fetch') },
    ],
    [t]
  )

  const SCHEDULE_DATE_FIELD_OPTIONS = useMemo(
    () => [
      { value: ScheduleDateField.Added, label: t('document_added') },
      { value: ScheduleDateField.Created, label: t('document_created') },
      { value: ScheduleDateField.Modified, label: t('document_modified') },
      { value: ScheduleDateField.CustomField, label: t('custom_field') },
    ],
    [t]
  )

  // Data for dropdowns
  const [correspondents, setCorrespondents] = useState<any[]>([])
  const [documentTypes, setDocumentTypes] = useState<any[]>([])
  const [storagePaths, setStoragePaths] = useState<any[]>([])
  const [workflowStatuses, setWorkflowStatuses] = useState<any[]>([])
  const [customFields, setCustomFields] = useState<any[]>([])
  const [mailRules, setMailRules] = useState<any[]>([])

  const [formData, setFormData] = useState<WorkflowTrigger>({
    type: WorkflowTriggerType.Consumption,
    filter_filename: '',
    sources: [],
    filter_path: '',
    filter_mailrule: undefined,
    match: '',
    matching_algorithm: MATCH_NONE,
    is_insensitive: true,
    filters: [],
  })

  const isEdit = !!trigger

  const convertCustomFieldQueryToModel = (queryString: string | null): CustomFieldQueriesModel => {
    const model = new CustomFieldQueriesModel()
    if (!queryString) return model

    try {
      const parsed = JSON.parse(queryString)
      if (!Array.isArray(parsed) || parsed.length === 0) return model

      const parseToCustomFieldElement = (item: any): any => {
        if (Array.isArray(item)) {
          if (item.length === 3) {
            return {
              type: CustomFieldQueryElementType.Atom,
              atom: {
                field: item[0],
                operator: item[1],
                value: item[2],
              },
            }
          } else if (item.length === 2) {
            const [operator, elements] = item
            if (Array.isArray(elements)) {
              return {
                type: CustomFieldQueryElementType.Expression,
                expression: {
                  operator: operator,
                  elements: elements.map((el) => parseToCustomFieldElement(el)),
                },
              }
            }
          }
        }
        return null
      }

      if (parsed.length === 2 && Array.isArray(parsed[1])) {
        const [operator, elements] = parsed
        model.logicalOperator = operator as 'OR' | 'AND' | 'NOT'
        if (Array.isArray(elements)) {
          const parsedElements = elements.map((el) => parseToCustomFieldElement(el)).filter((e: any): e is CustomFieldQueryElement => e !== null)
          const expression: CustomFieldQueryElement = {
            type: CustomFieldQueryElementType.Expression,
            expression: {
              operator: operator as 'OR' | 'AND' | 'NOT',
              elements: parsedElements,
            },
          }
          model.queries = [expression]
        }
      } else {
        const parsedElements = parsed.map((item: any) => parseToCustomFieldElement(item)).filter((e: any): e is CustomFieldQueryElement => e !== null)
        if (parsedElements.length > 0) {
          const expression: CustomFieldQueryElement = {
            type: CustomFieldQueryElementType.Expression,
            expression: {
              operator: model.logicalOperator,
              elements: parsedElements,
            },
          }
          model.queries = [expression]
        }
      }
    } catch (e) {}

    return model
  }

  const convertModelToCustomFieldQuery = (model: CustomFieldQueriesModel): string | null => {
    const serialized = model.serialize()
    return serialized || null
  }

  const buildFiltersFromTrigger = (trigger: WorkflowTrigger): WorkflowTriggerFilter[] => {
    const filters: WorkflowTriggerFilter[] = []

    // Check for tags filters
    if (trigger.filter_has_tags && trigger.filter_has_tags.length > 0) {
      filters.push({
        type: TriggerFilterType.TagsAny,
        values: trigger.filter_has_tags,
      })
    }

    if (trigger.filter_has_all_tags && trigger.filter_has_all_tags.length > 0) {
      filters.push({
        type: TriggerFilterType.TagsAll,
        values: trigger.filter_has_all_tags,
      })
    }

    if (trigger.filter_has_not_tags && trigger.filter_has_not_tags.length > 0) {
      filters.push({
        type: TriggerFilterType.TagsNone,
        values: trigger.filter_has_not_tags,
      })
    }

    // Check for correspondent filters
    if (trigger.filter_has_correspondent) {
      filters.push({
        type: TriggerFilterType.CorrespondentIs,
        values: trigger.filter_has_correspondent,
      })
    }

    if (trigger.filter_has_not_correspondents && trigger.filter_has_not_correspondents.length > 0) {
      filters.push({
        type: TriggerFilterType.CorrespondentNot,
        values: trigger.filter_has_not_correspondents,
      })
    }

    // Check for document type filters
    if (trigger.filter_has_document_type) {
      filters.push({
        type: TriggerFilterType.DocumentTypeIs,
        values: trigger.filter_has_document_type,
      })
    }

    if (trigger.filter_has_not_document_types && trigger.filter_has_not_document_types.length > 0) {
      filters.push({
        type: TriggerFilterType.DocumentTypeNot,
        values: trigger.filter_has_not_document_types,
      })
    }

    // Check for storage path filters
    if (trigger.filter_has_storage_path) {
      filters.push({
        type: TriggerFilterType.StoragePathIs,
        values: trigger.filter_has_storage_path,
      })
    }

    if (trigger.filter_has_not_storage_paths && trigger.filter_has_not_storage_paths.length > 0) {
      filters.push({
        type: TriggerFilterType.StoragePathNot,
        values: trigger.filter_has_not_storage_paths,
      })
    }

    // Check for custom field query
    if (trigger.filter_custom_field_query) {
      const model = convertCustomFieldQueryToModel(trigger.filter_custom_field_query)
      filters.push({
        type: TriggerFilterType.CustomFieldQuery,
        values: trigger.filter_custom_field_query,
        customFieldQueryModel: model,
      })
    }

    return filters
  }

  useEffect(() => {
    loadData()

    if (trigger) {
      // Build filters from existing trigger data
      const filters = buildFiltersFromTrigger(trigger)
      // Ensure matching_algorithm is set, default to MATCH_NONE if not present
      const cleanedTrigger = {
        ...trigger,
        matching_algorithm: trigger.matching_algorithm ?? MATCH_NONE,
        filters: filters,
      }
      setFormData(cleanedTrigger)
    } else {
      setFormData({
        type: WorkflowTriggerType.Consumption,
        filter_filename: '',
        sources: [],
        filter_path: '',
        filter_mailrule: undefined,
        match: '',
        matching_algorithm: MATCH_NONE,
        is_insensitive: true,
        filters: [],
      })
    }
    setErrors({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const loadData = async () => {
    try {
      const [correspondentsRes, documentTypesRes, storagePathsRes, workflowStatusesRes, customFieldsRes, mailRulesRes] = await Promise.all([
        correspondentService.listAll(),
        documentTypeService.listAll(),
        storagePathService.listAll(),
        workflowStatusService.listAll().then((r: ApiResponse<WorkflowStatus>) => r.results ?? []),
        customFieldService.listAll(),
        mailRuleService.listAll({ page_size: 100000 }),
      ])

      setCorrespondents(correspondentsRes.results)
      setDocumentTypes(documentTypesRes.results)
      setStoragePaths(storagePathsRes.results)
      setWorkflowStatuses(Array.isArray(workflowStatusesRes) ? workflowStatusesRes : [])
      setCustomFields(customFieldsRes.results)
      setMailRules(mailRulesRes.results)
    } catch (error) {}
  }

  const handleInputChange = (field: keyof WorkflowTrigger, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      // Map filters to trigger aggregate format
      const aggregate = {
        filter_has_tags: [] as number[],
        filter_has_all_tags: [] as number[],
        filter_has_not_tags: [] as number[],
        filter_has_not_correspondents: [] as number[],
        filter_has_not_document_types: [] as number[],
        filter_has_not_storage_paths: [] as number[],
        filter_has_not_workflow_statuses: [] as number[],
        filter_has_correspondent: null as number | null,
        filter_has_document_type: null as number | null,
        filter_has_storage_path: null as number | null,
        filter_workflow_status: null as number | null,
        filter_custom_field_query: null as string | null,
      }

      // Process filters
      let hasCustomFieldQueryFilter = false
      let hasCorrespondentIsFilter = false
      let hasDocumentTypeIsFilter = false
      let hasStoragePathIsFilter = false

      if (formData.filters && formData.filters.length > 0) {
        for (const filter of formData.filters) {
          switch (filter.type) {
            case TriggerFilterType.CustomFieldQuery:
              hasCustomFieldQueryFilter = true
              if ((filter as any).customFieldQueryModel) {
                const model = (filter as any).customFieldQueryModel as CustomFieldQueriesModel
                if (model && !model.isEmpty() && model.isValid()) {
                  const serialized = convertModelToCustomFieldQuery(model)
                  if (serialized && serialized.trim().length > 0) {
                    aggregate.filter_custom_field_query = serialized
                  } else {
                    aggregate.filter_custom_field_query = null
                  }
                } else {
                  aggregate.filter_custom_field_query = null
                }
              } else if (filter.values && typeof filter.values === 'string' && filter.values.trim().length > 0) {
                aggregate.filter_custom_field_query = filter.values as string
              } else {
                aggregate.filter_custom_field_query = null
              }
              break
            case TriggerFilterType.TagsAny:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_tags = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
            case TriggerFilterType.TagsAll:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_all_tags = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
            case TriggerFilterType.TagsNone:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_not_tags = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
            case TriggerFilterType.CorrespondentIs:
              hasCorrespondentIsFilter = true
              if (filter.values !== null && filter.values !== undefined) {
                aggregate.filter_has_correspondent = Array.isArray(filter.values) ? (filter.values[0] ?? null) : filter.values
              } else {
                aggregate.filter_has_correspondent = null
              }
              break
            case TriggerFilterType.CorrespondentNot:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_not_correspondents = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
            case TriggerFilterType.DocumentTypeIs:
              hasDocumentTypeIsFilter = true
              if (filter.values !== null && filter.values !== undefined) {
                aggregate.filter_has_document_type = Array.isArray(filter.values) ? (filter.values[0] ?? null) : filter.values
              } else {
                aggregate.filter_has_document_type = null
              }
              break
            case TriggerFilterType.DocumentTypeNot:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_not_document_types = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
            case TriggerFilterType.StoragePathIs:
              hasStoragePathIsFilter = true
              if (filter.values !== null && filter.values !== undefined) {
                aggregate.filter_has_storage_path = Array.isArray(filter.values) ? (filter.values[0] ?? null) : filter.values
              } else {
                aggregate.filter_has_storage_path = null
              }
              break
            case TriggerFilterType.StoragePathNot:
              if (filter.values && (Array.isArray(filter.values) ? filter.values.length > 0 : true)) {
                aggregate.filter_has_not_storage_paths = Array.isArray(filter.values) ? [...filter.values] : [filter.values]
              }
              break
          }
        }
      }

      aggregate.filter_workflow_status = formData.filter_workflow_status ?? null
      aggregate.filter_has_not_workflow_statuses = formData.filter_has_not_workflow_statuses || []

      // If filters don't exist, explicitly set to null (needed for deletion)
      if (!hasCustomFieldQueryFilter) {
        aggregate.filter_custom_field_query = null
      }
      if (!hasCorrespondentIsFilter) {
        aggregate.filter_has_correspondent = null
      }
      if (!hasDocumentTypeIsFilter) {
        aggregate.filter_has_document_type = null
      }
      if (!hasStoragePathIsFilter) {
        aggregate.filter_has_storage_path = null
      }

      // Create final trigger data - explicitly remove filter_custom_field_query from formData
      const { filter_custom_field_query: _, ...formDataWithoutCustomFieldQuery } = formData
      const triggerData = {
        ...formDataWithoutCustomFieldQuery,
        ...aggregate,
        filter_custom_field_query: hasCustomFieldQueryFilter ? (aggregate.filter_custom_field_query ?? null) : null,
        filter_workflow_status: aggregate.filter_workflow_status ?? null,
        filter_has_not_workflow_statuses: aggregate.filter_has_not_workflow_statuses || [],
        filters: undefined, // Remove filters from final data
      }

      onSuccess(triggerData)
    } catch (error: any) {
      onError(error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    return isEdit ? t('edit_trigger') : t('add_trigger')
  }

  const handleDelete = () => {
    if (onDelete) {
      onClose()
      onDelete()
    }
  }

  const canAddFilter = (): boolean => {
    const usedTypes = new Set(formData.filters?.map((filter) => filter.type) || [])

    return FILTER_TYPES.some((type) => {
      return !usedTypes.has(type.value)
    })
  }

  const getFilterTypeOptions = (filterIndex: number) => {
    const usedTypes = new Set(formData.filters?.filter((_, index) => index !== filterIndex).map((filter) => filter.type) || [])

    const currentType = formData.filters?.[filterIndex]?.type

    return FILTER_TYPES.map((type) => ({
      ...type,
      disabled: usedTypes.has(type.value) && type.value !== currentType,
    }))
  }

  const addFilter = () => {
    const usedTypes = new Set(formData.filters?.map((filter) => filter.type) || [])

    const availableType = FILTER_TYPES.find((type) => !usedTypes.has(type.value))

    if (!availableType) {
      return
    }

    const newFilter = {
      type: availableType.value,
      values: [],
      queryMode: 'OR',
    }
    setFormData((prev) => ({
      ...prev,
      filters: [...(prev.filters || []), newFilter],
    }))
  }

  const removeFilter = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      filters: prev.filters?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateFilter = (index: number, field: keyof any, value: any) => {
    setFormData((prev) => ({
      ...prev,
      filters: prev.filters?.map((filter, i) => (i === index ? { ...filter, [field]: value } : filter)) || [],
    }))
  }

  const getFilterSelectItemsForInput = (filterType: TriggerFilterType) => {
    switch (filterType) {
      case TriggerFilterType.CorrespondentIs:
      case TriggerFilterType.CorrespondentNot:
        return correspondents.map((c) => ({ id: c.id, label: c.name, name: c.name }))
      case TriggerFilterType.DocumentTypeIs:
      case TriggerFilterType.DocumentTypeNot:
        return documentTypes.map((dt) => ({ id: dt.id, label: dt.name, name: dt.name }))
      case TriggerFilterType.StoragePathIs:
      case TriggerFilterType.StoragePathNot:
        return storagePaths.map((sp) => ({ id: sp.id, label: sp.name, name: sp.name }))
      default:
        return []
    }
  }

  const isTagsFilter = (filterType: TriggerFilterType) => {
    return [TriggerFilterType.TagsAny, TriggerFilterType.TagsAll, TriggerFilterType.TagsNone].includes(filterType)
  }

  const isCustomFieldQueryFilter = (filterType: TriggerFilterType) => {
    return filterType === TriggerFilterType.CustomFieldQuery
  }

  const isSelectMultiple = (filterType: TriggerFilterType) => {
    return [
      TriggerFilterType.CorrespondentNot,
      TriggerFilterType.DocumentTypeNot,
      TriggerFilterType.StoragePathNot,
    ].includes(filterType)
  }

  const matchingPatternRequired = () => {
    return formData.matching_algorithm !== MATCH_AUTO && formData.matching_algorithm !== MATCH_NONE
  }

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static" className="trigger-edit-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Trigger Type */}
          <InputSelect
            title={t('trigger_type')}
            value={formData.type}
            onChange={(value) => handleInputChange('type', value)}
            items={TRIGGER_TYPES.map((type) => ({ id: type.value, name: type.label }))}
            bindLabel="name"
            hint={t('trigger_type_description')}
            error={errors.type}
          />

          {/* Document workflow status changed trigger description */}
          {formData.type === WorkflowTriggerType.DocumentWorkflowStatusChanged && (
            <p className="small text-muted">{t('trigger_type_document_workflow_status_changed_description')}</p>
          )}

          {/* Scheduled Trigger Fields */}
          {formData.type === WorkflowTriggerType.Scheduled && (
            <>
              <p className="small">{t('scheduled_trigger_description')}</p>
              <Row>
                <Col md={2}>
                  <InputText
                    title={t('offset_days')}
                    type="number"
                    value={formData.schedule_offset_days || ''}
                    onChange={(value) => handleInputChange('schedule_offset_days', value ? parseInt(value) : undefined)}
                    placeholder="0"
                    hint={t('offset_days_description')}
                  />
                </Col>
                <Col md={4}>
                  <InputSelect
                    title={t('relative_to')}
                    value={formData.schedule_date_field}
                    onChange={(value) => handleInputChange('schedule_date_field', value)}
                    items={SCHEDULE_DATE_FIELD_OPTIONS.map((option) => ({ id: option.value, name: option.label }))}
                    bindLabel="name"
                  />
                </Col>
                {formData.schedule_date_field === ScheduleDateField.CustomField && (
                  <Col md={3}>
                    <InputSelect
                      title={t('custom_field')}
                      value={formData.schedule_date_custom_field}
                      onChange={(value) => handleInputChange('schedule_date_custom_field', value)}
                      items={customFields}
                      bindLabel="name"
                      allowNull={true}
                      placeholder={t('select_custom_field')}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <InputSwitch
                    title={t('recurring')}
                    value={formData.schedule_is_recurring || false}
                    onChange={(value) => handleInputChange('schedule_is_recurring', value)}
                    horizontal={true}
                  />
                </Col>
                {formData.schedule_is_recurring && (
                  <Col md={3}>
                    <InputText
                      title={t('recurring_interval_days')}
                      type="number"
                      value={formData.schedule_recurring_interval_days || ''}
                      onChange={(value) => handleInputChange('schedule_recurring_interval_days', value ? parseInt(value) : undefined)}
                      placeholder="1"
                    />
                  </Col>
                )}
              </Row>
            </>
          )}

          <p className="small">{t('trigger_filters_description')}</p>

          {/* Basic Filters */}
          <Row>
            <Col>
              <InputText
                title={t('filter_filename')}
                value={formData.filter_filename || ''}
                onChange={(value) => handleInputChange('filter_filename', value)}
                error={errors.filter_filename}
                placeholder="*.pdf"
                hint={t('filter_filename_description')}
              />

              {/* Consumption-specific filters */}
              {formData.type === WorkflowTriggerType.Consumption && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('filter_sources')}</Form.Label>
                    <Select
                      isMulti
                      options={SOURCE_OPTIONS}
                      value={SOURCE_OPTIONS.filter((option) => (formData.sources || []).includes(option.value))}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions ? selectedOptions.map((option) => option.value) : []
                        handleInputChange('sources', values)
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder={t('select_sources')}
                    />
                    <Form.Text className="text-muted">{t('filter_sources_description')}</Form.Text>
                  </Form.Group>

                  <InputText
                    title={t('filter_path')}
                    value={formData.filter_path || ''}
                    onChange={(value) => handleInputChange('filter_path', value)}
                    error={errors.filter_path}
                    placeholder="/invoices/*"
                    hint={t('filter_path_description')}
                    autocomplete="off"
                  />

                  <Form.Group className="mb-3">
                    <Form.Label>{t('filter_mail_rule')}</Form.Label>
                    <Form.Select
                      value={formData.filter_mailrule || ''}
                      onChange={(e) => handleInputChange('filter_mailrule', e.target.value ? parseInt(e.target.value) : undefined)}
                    >
                      <option value="">{t('select_mail_rule')}</option>
                      {mailRules.map((rule) => (
                        <option key={rule.id} value={rule.id}>
                          {rule.name || `Mail Rule ${rule.id}`}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">{t('filter_mail_rule_description')}</Form.Text>
                  </Form.Group>
                </>
              )}

              {/* Content matching for DocumentAdded, DocumentUpdated, Scheduled, DocumentWorkflowStatusChanged */}
              {(formData.type === WorkflowTriggerType.DocumentAdded ||
                formData.type === WorkflowTriggerType.DocumentUpdated ||
                formData.type === WorkflowTriggerType.Scheduled ||
                formData.type === WorkflowTriggerType.DocumentWorkflowStatusChanged) && (
                <>
                  <InputSelect
                    title={t('content_matching_algorithm')}
                    value={formData.matching_algorithm ?? MATCH_NONE}
                    onChange={(value) => handleInputChange('matching_algorithm', value)}
                    items={getMatchingAlgorithms(t)
                      .filter((algo) => algo.value !== MATCH_AUTO)
                      .map((algo) => ({ id: algo.value, name: algo.label }))}
                    bindLabel="name"
                  />

                  {matchingPatternRequired() && (
                    <>
                      <InputText
                        title={t('content_matching_pattern')}
                        value={formData.match || ''}
                        onChange={(value) => handleInputChange('match', value)}
                        placeholder="invoice"
                      />

                      <InputSwitch
                        title={t('case_insensitive')}
                        value={formData.is_insensitive || false}
                        onChange={(value) => handleInputChange('is_insensitive', value)}
                        horizontal={true}
                      />
                    </>
                  )}
                </>
              )}

              {/* Workflow status filters */}
              {(formData.type === WorkflowTriggerType.DocumentAdded ||
                formData.type === WorkflowTriggerType.DocumentUpdated ||
                formData.type === WorkflowTriggerType.Scheduled ||
                formData.type === WorkflowTriggerType.DocumentWorkflowStatusChanged) && (
                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('filter_document_workflow_status_is')}</Form.Label>
                      <Form.Select
                        value={formData.filter_workflow_status ?? ''}
                        onChange={(e) => handleInputChange('filter_workflow_status', e.target.value ? parseInt(e.target.value, 10) : null)}
                      >
                        <option value="">{t('any')}</option>
                        {workflowStatuses.map((ws) => (
                          <option key={ws.id} value={ws.id}>
                            {ws.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">{t('filter_document_workflow_status_is_description')}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('filter_document_workflow_status_not')}</Form.Label>
                      <Select
                        isMulti
                        options={workflowStatuses.map((ws) => ({ value: ws.id, label: ws.name }))}
                        value={(formData.filter_has_not_workflow_statuses || []).map((id) => {
                          const ws = workflowStatuses.find((s) => s.id === id)
                          return ws ? { value: ws.id, label: ws.name } : { value: id, label: String(id) }
                        })}
                        onChange={(selected) => handleInputChange('filter_has_not_workflow_statuses', selected ? selected.map((o) => o.value) : [])}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={t('select_workflow_statuses')}
                      />
                      <Form.Text className="text-muted">{t('filter_document_workflow_status_not_description')}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>

          {/* Advanced Filters */}
          {(formData.type === WorkflowTriggerType.DocumentAdded ||
            formData.type === WorkflowTriggerType.DocumentUpdated ||
            formData.type === WorkflowTriggerType.Scheduled ||
            formData.type === WorkflowTriggerType.DocumentWorkflowStatusChanged) && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-3">
                <Form.Label className="mb-0">{t('advanced_filters')}</Form.Label>
                <Button type="button" variant="outline-primary" size="sm" className="ms-auto" onClick={addFilter} disabled={!canAddFilter()}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_filter')}
                </Button>
              </div>

              {formData.filters && formData.filters.length > 0 ? (
                <ListGroup>
                  {formData.filters.map((filter, index) => (
                    <ListGroup.Item key={index} className="d-flex align-items-center gap-2">
                      <div className="w-25">
                        <Form.Select value={filter.type} onChange={(e) => updateFilter(index, 'type', e.target.value)}>
                          {getFilterTypeOptions(index).map((type) => (
                            <option key={type.value} value={type.value} disabled={type.disabled}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="flex-grow-1">
                        {isTagsFilter(filter.type) ? (
                          <InputTags
                            value={Array.isArray(filter.values) ? filter.values : []}
                            onChange={(value) => updateFilter(index, 'values', value)}
                            multiple={true}
                          />
                        ) : isCustomFieldQueryFilter(filter.type) ? (
                          (() => {
                            const getCustomFieldQueryModel = (): CustomFieldQueriesModel => {
                              if ((filter as any).customFieldQueryModel) {
                                return (filter as any).customFieldQueryModel
                              }
                              const model = new CustomFieldQueriesModel()
                              if (filter.values) {
                                try {
                                  const parsed = JSON.parse(filter.values as string)
                                  if (Array.isArray(parsed) && parsed.length === 2) {
                                    const [operator, elements] = parsed
                                    if (Array.isArray(elements)) {
                                      const expression: CustomFieldQueryElement = {
                                        type: CustomFieldQueryElementType.Expression,
                                        expression: {
                                          operator: operator as 'OR' | 'AND' | 'NOT',
                                          elements: elements
                                            .map((el: any): CustomFieldQueryElement | null => {
                                              if (Array.isArray(el) && el.length === 3) {
                                                return {
                                                  type: CustomFieldQueryElementType.Atom,
                                                  atom: { field: el[0], operator: el[1], value: el[2] },
                                                }
                                              } else if (Array.isArray(el) && el.length === 2) {
                                                const [op, elems] = el
                                                return {
                                                  type: CustomFieldQueryElementType.Expression,
                                                  expression: {
                                                    operator: op,
                                                    elements: elems
                                                      .map((e: any) => {
                                                        if (Array.isArray(e) && e.length === 3) {
                                                          return {
                                                            type: CustomFieldQueryElementType.Atom,
                                                            atom: { field: e[0], operator: e[1], value: e[2] },
                                                          }
                                                        }
                                                        return null
                                                      })
                                                      .filter((e: any): e is CustomFieldQueryElement => e !== null),
                                                  },
                                                }
                                              }
                                              return null
                                            })
                                            .filter((e: any): e is CustomFieldQueryElement => e !== null),
                                        },
                                      }
                                      model.queries = [expression]
                                    }
                                  }
                                } catch {}
                              }
                              ;(filter as any).customFieldQueryModel = model
                              return model
                            }
                            const model = getCustomFieldQueryModel()
                            return (
                              <CustomFieldsQueryDropdown
                                selectionModel={model}
                                onSelectionModelChange={(updatedModel) => {
                                  updateFilter(index, 'customFieldQueryModel', updatedModel)
                                  const serialized = updatedModel.serialize()
                                  updateFilter(index, 'values', serialized || null)
                                }}
                                useDropdown={false}
                              />
                            )
                          })()
                        ) : isSelectMultiple(filter.type) ? (
                          <InputSelect
                            value={Array.isArray(filter.values) ? filter.values : []}
                            onChange={(value) => updateFilter(index, 'values', value)}
                            items={getFilterSelectItemsForInput(filter.type)}
                            bindLabel="label"
                            multiple={true}
                            placeholder={t('select_option')}
                          />
                        ) : (
                          <InputSelect
                            value={Array.isArray(filter.values) ? filter.values[0] : filter.values}
                            onChange={(value) => updateFilter(index, 'values', value)}
                            items={getFilterSelectItemsForInput(filter.type)}
                            bindLabel="label"
                            allowNull={true}
                            placeholder={t('select_option')}
                          />
                        )}
                      </div>
                      <Button type="button" variant="link" className="text-danger p-0" onClick={() => removeFilter(index)} title={t('delete')}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p className="text-muted small">{t('no_advanced_filters')}</p>
              )}
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {isEdit && onDelete && (
              <IfPermissions action={PermissionAction.Delete} type={PermissionType.Workflow}>
                <Button variant="outline-danger" onClick={handleDelete} disabled={loading}>
                  <i className="bi bi-trash me-1"></i>
                  {t('delete')}
                </Button>
              </IfPermissions>
            )}
          </div>
          <div>
            <Button variant="outline-secondary" onClick={onClose} disabled={loading} className="me-2">
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {t('save')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
